/**
 * Color combination ideas — mobile image scroller.
 *
 * On mobile/tablet the section renders a single horizontal scroll-snap track
 * with one snap point per product image (across all products), plus a stack of
 * per-product info panels below it. This element keeps the visible info panel in
 * sync with the image currently in view: swiping through a product's images
 * leaves its info panel up, and crossing into the next product's first image
 * swaps in that product's panel.
 *
 * A single scroller (rather than nested scrollers) is what makes the
 * "swipe images, then continue to the next product" gesture work reliably on
 * touch devices — there is no inner/outer scroll-chaining to fight.
 */
if (!customElements.get('color-combination-mobile')) {
  class ColorCombinationMobile extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('.cci-mobile__images');
      this.cells = this.scroller
        ? Array.from(this.scroller.querySelectorAll('.cci-mobile__image'))
        : [];
      this.panels = Array.from(this.querySelectorAll('.cci-mobile__panel'));

      if (!this.scroller || this.cells.length === 0 || this.panels.length === 0) {
        return;
      }

      this._frame = null;
      this._onScroll = this._onScroll.bind(this);
      this.scroller.addEventListener('scroll', this._onScroll, { passive: true });
      this._sync();
    }

    disconnectedCallback() {
      if (this.scroller) {
        this.scroller.removeEventListener('scroll', this._onScroll);
      }
      if (this._frame) cancelAnimationFrame(this._frame);
    }

    _onScroll() {
      if (this._frame) return;
      this._frame = requestAnimationFrame(() => {
        this._frame = null;
        this._sync();
      });
    }

    _sync() {
      if (!this.scroller.clientWidth) return;

      // The image whose centre is nearest the viewport centre is "active".
      const viewportCenter = this.scroller.scrollLeft + this.scroller.clientWidth / 2;
      let activeCell = this.cells[0];
      let smallestDistance = Infinity;
      for (const cell of this.cells) {
        const cellCenter = cell.offsetLeft + cell.offsetWidth / 2;
        const distance = Math.abs(cellCenter - viewportCenter);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          activeCell = cell;
        }
      }

      const activeIndex = activeCell.getAttribute('data-product-index');
      for (const panel of this.panels) {
        const isActive = panel.getAttribute('data-product-index') === activeIndex;
        panel.toggleAttribute('hidden', !isActive);
      }
    }
  }

  customElements.define('color-combination-mobile', ColorCombinationMobile);
}
